import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

import { cn } from '@/core/string.core';

interface PopoverContextValue {
  triggerRef: React.RefObject<HTMLElement>;
}

const PopoverContext = React.createContext<PopoverContextValue | null>(null);

interface PopoverProps extends React.ComponentProps<typeof PopoverPrimitive.Root> {
  autoFocusOnClose?: boolean;
}

const PopoverRoot = ({ autoFocusOnClose = false, onOpenChange, ...props }: PopoverProps) => {
  const triggerRef = React.useRef<HTMLElement>(null);
  const isUnmountingRef = React.useRef(false);

  const contextValue = React.useMemo(() => ({ triggerRef }), []);

  React.useEffect(() => {
    return () => {
      isUnmountingRef.current = true;
    };
  }, []);

  const handleOpenChange = React.useCallback(
    (isOpen: boolean) => {
      if (!isOpen && autoFocusOnClose && triggerRef.current && !isUnmountingRef.current) {
        // Use requestAnimationFrame to ensure the popover has fully closed
        requestAnimationFrame(() => {
          if (triggerRef.current && !isUnmountingRef.current) {
            triggerRef.current.focus();
          }
        });
      }
      onOpenChange?.(isOpen);
    },
    [autoFocusOnClose, onOpenChange],
  );

  return (
    <PopoverContext.Provider value={contextValue}>
      <PopoverPrimitive.Root onOpenChange={handleOpenChange} {...props} />
    </PopoverContext.Provider>
  );
};

const usePopoverContext = () => {
  const context = React.useContext(PopoverContext);
  if (!context) {
    throw new Error('usePopoverContext must be used within a Popover');
  }
  return context;
};

const Popover = PopoverRoot;

const PopoverTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>(({ ...props }, ref) => {
  const { triggerRef } = usePopoverContext();

  return (
    <PopoverPrimitive.Trigger
      ref={(node) => {
        if (triggerRef) {
          (triggerRef as React.MutableRefObject<HTMLElement | null>).current = node;
        }
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLElement | null>).current = node;
        }
      }}
      {...props}
    />
  );
});
PopoverTrigger.displayName = PopoverPrimitive.Trigger.displayName;
const PopoverClose = PopoverPrimitive.Close;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & { id?: string }
>(({ className, align = 'center', sideOffset = 4, id, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      role="dialog"
      id={id}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'shadow z-50 w-72 rounded border border-other-stroke bg-white p-3 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

// usePopoverContext is a hook closely tied to the Popover component, so it's exported here for convenience
// eslint-disable-next-line react-refresh/only-export-components
export { Popover, PopoverClose, PopoverContent, PopoverTrigger, usePopoverContext };
